import { useKaraokeStore } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { CAPTION_FONTS_BY_SCRIPT } from '@src/utility/captionFonts';
import {
  isOriginalLang,
  resolveCaptionLanguageKey,
  resolveCaptionScript,
} from '@src/utility/captionLanguage';
import { useSearchParams } from 'react-router-dom';

import type { CaptionScript } from '@src/utility/captionLanguage';

/** Short user-facing label per script, for picker hints. */
export const CAPTION_SCRIPT_LABELS: Record<CaptionScript, string> = {
  latin: 'Latin',
  cyrillic: 'Cyrillic',
  japanese: 'Japanese',
  'chinese-simplified': 'Chinese',
  'chinese-traditional': 'Chinese',
  korean: 'Korean',
};

/**
 * PLAT-11415 — the active caption script, its curated fonts, and a short
 * label, for the captions font picker. Generalizes PLAT-10420's
 * Japanese-only gate (`useShowJapaneseFonts`, retired by this hook).
 *
 * The active language is the produced caption track's
 * `KaraokeConfig.language` when it names a real language; the
 * `'original'`/`'english'` sentinels defer to the transcript view's `?lang`
 * (a transcript translated before the captions language is switched to
 * match) and, past that, resolve to the SOURCE language via
 * `resolveCaptionLanguageKey` — a Russian source video's "original"
 * captions are Cyrillic, and the engine (PLAT-11409/11413) substitutes
 * accordingly, so the picker must follow the same rule. `latin` means "no
 * extra section, no coverage flags" (the main `FONTS` list already serves
 * Latin).
 *
 * MUST be called inside a `mobx-react-lite` `observer` so the
 * `defaultKaraokeConfig.language` / transcript-language reads are reactive.
 */
export const useCaptionScriptFonts = (): {
  script: CaptionScript;
  scriptFonts: readonly string[];
  scriptLabel: string;
} => {
  const karaokeStore = useKaraokeStore();
  const videoCreator = useVideoCreatorStore();
  const [searchParams] = useSearchParams();
  const configLanguage = karaokeStore.defaultKaraokeConfig?.language;
  const activeLanguage =
    configLanguage && !isOriginalLang(configLanguage)
      ? configLanguage
      : searchParams.get('lang');
  const script = resolveCaptionScript(
    resolveCaptionLanguageKey(
      activeLanguage,
      videoCreator.rootStore?.transcriptionStore?.rawLanguage,
      videoCreator.story?.originalVideoLanguage,
    ),
  );
  return {
    script,
    scriptFonts: CAPTION_FONTS_BY_SCRIPT[script],
    scriptLabel: CAPTION_SCRIPT_LABELS[script],
  };
};
